
import {
  defineComponent,
  ref,
} from '@vue/composition-api';
import { Video, FCButton } from '@fc/angie-ui';
import FCSwiper from '~/components/FCSwiper.vue';
import FCSwiperProgressBar from '~/components/FCSwiperProgressBar.vue';
import Section from '~/components/v5/Section.vue';
import SectionHeader from '~/components/v5/SectionHeader.vue';
import { isMobileEffect } from '~/effects/matchMedia';
import { useEquipmentSectionModal } from '~/composables/useEquipmentSectionModal';

export default defineComponent({
  name: 'TheEquipmentSection',
  components: {
    FCSwiper,
    FCSwiperProgressBar,
    Section,
    SectionHeader,
    Video,
    FCButton,
  },
  setup() {
    const { openEquipmentModal } = useEquipmentSectionModal();

    const systemCards = [
      {
        id: 1,
        heading: 'The Bag',
        description: 'A true freestanding bag that stays put when you throw real punches and kicks. No drilling. No ceiling mounts. Sets up easily and takes a beating, round after round. <br><br> Tested by fighters (and even investors like Mike Tyson) for stability and feel without the hassle of a hanging bag.',
        videoSrcWebm: 'https://cdn-b.joinfightcamp.com/videos/16x9_BAG.webm',
        videoSrc: 'https://cdn-b.joinfightcamp.com/videos/16x9_BAG_mobile.mp4',
        firstFrameUrl: 'https://a.storyblok.com/f/152150/3840x2160/12ef1fd8d2/bag.jpeg',
        modalSubtitle: 'Sets up easily without mounts. The only freestanding bag built to take true power behind punches and kicks round after round.',
        modalFeaturesTitle: 'Specs',
        modalFeatures: [
          'Height: 67" fully assembled',
          'Top Diameter: 18"',
          'Base Diameter: 24"',
          'Materials: High-density durable foam core, vinyl cover',
          'Assembly: Hook-and-loop belts secure top to base',
          'Weight: Fully assembled w/sand ~380 lb',
          'Mobility: Tilt and roll to position even when full',
        ],
      },
      {
        id: 2,
        heading: 'The Gloves',
        description: 'Engineered for heavy rounds and fast work: breathable palms for comfort and odor control, multi-layer foam to take impact, and a rock-solid wrist wrap that locks you in.',
        videoSrcWebm: 'https://cdn-b.joinfightcamp.com/videos/16x9_GLOVES_V2.webm',
        videoSrc: 'https://cdn-b.joinfightcamp.com/videos/16x9_GLOVES_V2.mp4',
        firstFrameUrl: 'https://a.storyblok.com/f/152150/3840x2160/ebdce27da1/gloves.jpeg',
        modalSubtitle: 'Premium leather, multi-layer padding, and a secure hook-and-loop wrist keep your hands protected and your form precise—round after round. Ventilated palms keep hands comfortable and help manage odor during long sessions.',
        modalFeatureSections: [
          {
            title: 'Size & Fit',
            items: [
              'Available in <span class="fc-bold">12 oz</span> and <span class="fc-bold">16 oz</span> (oz = padding weight)',
              'Adults Under ~5\'9" / ~170 lb: typically 12 oz',
              'Adults Over ~5\'9" / ~170 lb: typically 16 oz',
              'Ergonomic fit with wrist support and multi-layer padding',
              'Kids size available (≈ age 12 and under)',
            ],
          },
          {
            title: 'Materials & Care',
            items: [
              'Made from premium leather',
              'Wipe clean with a damp cloth; do not machine wash',
            ],
          },
        ],
      },

      {
        id: 3,
        heading: 'The Bag Ring',
        description: 'Patented design. Home-safe, fight-ready.Engineered to hold the FightCamp Bag steady under real power and shield your floors so your focus stays on the work, not the setup.',
        videoSrcWebm: 'https://cdn-b.joinfightcamp.com/videos/16x9_BAGRING_V2.webm',
        videoSrc: 'https://cdn-b.joinfightcamp.com/videos/16x9_BAGRING_V2.mp4',
        firstFrameUrl: 'https://a.storyblok.com/f/152150/3840x2160/5cd8b650af/bag_ring.jpeg',
        modalSubtitle: 'Lock your target in place. Our patented Bag Ring keeps the FightCamp Bag centered through full-power punches and kicks—no sliding, no mid-round adjustments. All while protecting your floors.',
        modalFeatureSections: [
          {
            title: 'Features',
            items: [
              'Patented ring prevents the FightCamp Bag from sliding or shifting during training',
              'Keeps your target centered for consistent alignment and power delivery',
              'Protects flooring from scuffs and damage',
            ],
          },
          {
            title: 'Size & Materials',
            items: [
              '<span class="fc-bold">24" diameter</span> circular ring/mat',
              'Fits securely around the base of the FightCamp Bag',
              'Heavy-duty rubber mat with high-quality plastic bumpers',
              'Wipe clean with a damp cloth; store indoors when not in use',
            ],
          },
        ],
      },
      {
        id: 4,
        heading: 'The Sound Sleeve',
        description: 'Keep the power. Lose the noise.Engineered to reduce bag impact by up to -15 dB (≈70% quieter) so you can train when it works for you, whether it is before dawn, after bedtime, or with neighbors a wall away.',
        videoSrcWebm: 'https://cdn-b.joinfightcamp.com/videos/SoundSleeve_DB_NoTextCard_16x9_HD.webm',
        videoSrc: 'https://cdn-b.joinfightcamp.com/videos/SoundSleeve_DB_NoTextCard_16x9_HD.mp4',
        firstFrameUrl: 'https://a.storyblok.com/f/152150/3840x2160/161b5bc535/sound_sleeve.jpeg',
        hasAudio: true,
        modalSubtitle: 'Train hard. Stay quiet. <br>Cuts impact noise by up to  -15 dB (≈70% quieter to the human ear) and dampens vibration, so you can go early, go late, and keep peace with sleeping kids or close neighbors, without dulling your power.',
        modalFeatureSections: [
          {
            title: 'Specs',
            items: [
              '<span class="fc-bold">Noise Reduction:</span> Up to -15 dB (≈ 70% perceived quieter)',
              '<span class="fc-bold">Benefit:</span> Lower impact sound + vibration for shared spaces',
              '<span class="fc-bold">Use Cases:</span> Early mornings, late nights, apartments/condos, kids next door',
              '<span class="fc-bold">Compatibility:</span> Built for the FightCamp freestanding bag',
              '<span class="fc-bold">Install/Care:</span> Simple on/off zipper design; machine washable; indoor use recommended',
            ],
          },
        ],
      },
    ];

    const isMobile = isMobileEffect();
    const isModalOpen = ref(false);

    const currentSlide = ref(0);
    const autoplayDuration = 9000;
    const isPaused = ref(true); // Set to true to pause for debugging

    const getVideoPlaybackMode = (cardId: number) => (
      (currentSlide.value === cardId - 1 && !isModalOpen.value)
        ? 'auto'
        : 'none'
    );

    const handleSlideChange = (event: { activeIndex: number; isBeginning?: boolean; isEnd?: boolean }) => {
      currentSlide.value = event.activeIndex;
    };

    const handleOpenEquipmentModal = (cardId: number) => {
      const card = systemCards.find((c) => c.id === cardId);
      if (card) {
        isModalOpen.value = true;
        openEquipmentModal({
          title: card.heading,
          subtitle: card.modalSubtitle,
          featuresTitle: card.modalFeaturesTitle,
          features: card.modalFeatures,
          featureSections: card.modalFeatureSections,
          videoSrc: card.videoSrc,
          firstFrameUrl: card.firstFrameUrl,
          hasAudio: card.hasAudio,
        }, {
          onClose: () => {
            isModalOpen.value = false;
          },
        });
      }
    };

    return {
      systemCards,
      isMobile,
      isModalOpen,
      currentSlide,
      autoplayDuration,
      isPaused,
      getVideoPlaybackMode,
      handleSlideChange,
      handleOpenEquipmentModal,
    };
  },
});
